// Editor.js block tool for a content image, backed by the Media library.
// Choosing an image opens the shared picker (browse the library or upload)
// via the pickImage bridge instead of a bare file input. Stores the same
// shape @editorjs/image produced — { file: { key, url }, caption } — plus an
// optional display size { size: { value, unit: "%" | "px" | "rem" } }
// (absent / 100% = full width). Older blocks stored a bare widthPercent;
// loaded here as a %-size and migrated on save. The template renderer and
// canvas preview clamp the same ranges (%, 10–100; px, 20–2000; rem, 1–120).
//
// config: { pickImage }
//   pickImage() -> Promise<{ key, url, caption } | null>  (null = dismissed)

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';

const SIZE_UNITS = ["%", "px", "rem"];
// [min, max] per unit; rem values snap to quarter steps.
const SIZE_BOUNDS = { "%": [10, 100], px: [20, 2000], rem: [1, 120] };

function clampSizeValue(raw, unit) {
  const [min, max] = SIZE_BOUNDS[unit];
  const n = Number(raw);
  if (!Number.isFinite(n)) return unit === "%" ? 100 : min;
  const snapped = unit === "rem" ? Math.round(n * 4) / 4 : Math.round(n);
  return Math.min(Math.max(snapped, min), max);
}

/** Normalize stored size ({ size } or legacy widthPercent) to { value, unit }. */
function normalizeSize(data) {
  const raw = data?.size;
  if (raw && typeof raw === "object" && SIZE_UNITS.includes(raw.unit)) {
    return { value: clampSizeValue(raw.value, raw.unit), unit: raw.unit };
  }
  if (data?.widthPercent !== undefined) {
    return { value: clampSizeValue(data.widthPercent, "%"), unit: "%" };
  }
  return { value: 100, unit: "%" };
}

export class ImageTool {
  static get toolbox() {
    return { title: "Image", icon: ICON };
  }

  constructor({ data, config, block, api }) {
    this.block = block;
    this.api = api;
    this.config = config || {};
    // Preserve unknown fields (withBorder etc.) written by the previous tool.
    this.data = {
      ...data,
      file: { ...data?.file },
      caption: data?.caption ?? "",
      size: normalizeSize(data),
    };
    // Folded into `size` above; dropped so save() writes one shape.
    delete this.data.widthPercent;
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-image-tool";
    this._root = root;
    this._renderContent();
    return root;
  }

  _renderContent() {
    const root = this._root;
    root.innerHTML = "";

    if (this.data.file?.url) {
      const img = document.createElement("img");
      img.className = "pgs-image-tool__img";
      img.src = this.data.file.url;
      img.alt = this.data.caption || "";
      const applySize = () => {
        img.style.width = `${this.data.size.value}${this.data.size.unit}`;
      };
      applySize();

      // Display-size row: a % / px / rem unit toggle, then a slider (%) or a
      // number input (exact px/rem). Resizes the preview live; the same size
      // is applied on the published site. Switching unit converts from the
      // preview's current rendered width so the image doesn't jump.
      const sizeRow = document.createElement("div");
      sizeRow.className = "pgs-image-tool__size";
      const sizeLabel = document.createElement("span");
      sizeLabel.className = "pgs-image-tool__size-label";
      sizeLabel.textContent = "Size";

      const unitToggle = document.createElement("div");
      unitToggle.className = "pgs-image-tool__size-units";
      const control = document.createElement("div");
      control.className = "pgs-image-tool__size-control";

      const setSize = (value, unit) => {
        this.data.size = { value: clampSizeValue(value, unit), unit };
        applySize();
        this.block?.dispatchChange?.();
      };

      const renderControl = () => {
        control.innerHTML = "";
        const { value, unit } = this.data.size;
        if (unit === "%") {
          const slider = document.createElement("input");
          slider.type = "range";
          slider.className = "pgs-image-tool__size-slider";
          slider.min = "10";
          slider.max = "100";
          slider.step = "5";
          slider.value = String(value);
          slider.setAttribute("aria-label", "Image display width (percent)");
          const readout = document.createElement("span");
          readout.className = "pgs-image-tool__size-value";
          readout.textContent = `${value}%`;
          slider.addEventListener("input", () => {
            setSize(slider.value, "%");
            readout.textContent = `${this.data.size.value}%`;
          });
          control.append(slider, readout);
          return;
        }
        const input = document.createElement("input");
        input.type = "number";
        input.className = "pgs-image-tool__size-input";
        const [min, max] = SIZE_BOUNDS[unit];
        input.min = String(min);
        input.max = String(max);
        input.step = unit === "rem" ? "0.25" : "1";
        input.value = String(value);
        input.setAttribute("aria-label", `Image display width (${unit})`);
        // Clamp on change (not per keystroke) so typing isn't fought.
        input.addEventListener("input", () => setSize(input.value, unit));
        input.addEventListener("change", () => {
          input.value = String(this.data.size.value);
        });
        control.appendChild(input);
      };

      const unitButtons = SIZE_UNITS.map((unit) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "pgs-gallery-tool__btn";
        button.textContent = unit;
        button.addEventListener("click", () => {
          if (this.data.size.unit === unit) return;
          // Convert from the rendered width so the visual size carries over.
          const rendered = img.getBoundingClientRect().width;
          const column = img.parentElement?.getBoundingClientRect().width || 0;
          let value;
          if (unit === "px") value = rendered || 400;
          else if (unit === "rem") value = rendered ? rendered / 16 : 20;
          else value = rendered && column ? (rendered / column) * 100 : 100;
          setSize(value, unit);
          applyUnitButtons();
          renderControl();
        });
        unitToggle.appendChild(button);
        return { unit, button };
      });
      const applyUnitButtons = () => {
        for (const { unit, button } of unitButtons) {
          button.classList.toggle("pgs-gallery-tool__btn--active", this.data.size.unit === unit);
        }
      };
      applyUnitButtons();
      renderControl();

      sizeRow.append(sizeLabel, unitToggle, control);

      const caption = document.createElement("input");
      caption.className = "pgs-image-tool__caption";
      caption.type = "text";
      caption.placeholder = "Add a caption (optional)";
      caption.value = this.data.caption || "";
      caption.addEventListener("input", () => {
        this.data.caption = caption.value;
        this.block?.dispatchChange?.();
      });

      const replace = document.createElement("button");
      replace.type = "button";
      replace.className = "pgs-image-tool__replace";
      replace.textContent = "Replace image";
      replace.addEventListener("click", () => this._pick());

      root.append(img, sizeRow, caption, replace);
      return;
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "pgs-image-tool__choose";
    button.innerHTML = `${ICON}<span>Choose image</span>`;
    button.addEventListener("click", () => this._pick());
    root.appendChild(button);
  }

  async _pick() {
    const picked = await this.config.pickImage?.();
    if (!picked) return;
    this.data.file = { key: picked.key, url: picked.url };
    if (!this.data.caption && picked.caption) this.data.caption = picked.caption;
    this._renderContent();
    this.block?.dispatchChange?.();
  }

  save() {
    return { ...this.data };
  }

  validate(savedData) {
    // Drop empty image blocks (nothing was ever chosen).
    return Boolean(savedData.file?.url || savedData.file?.key);
  }
}
